import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { PhoneCall, Plus, Trash2, Loader2, Building2, Mail, Phone } from 'lucide-react';
import { CollapsibleSection } from './CollapsibleSection';

const EMPTY = { name: '', reason: '', company: '', phone: '', email: '' };

export const ReferenceCallsCard = ({ projectId }) => {
  const [refs, setRefs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.getReferenceCalls(projectId).then(r => setRefs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleAdd = async () => {
    if (!form.name.trim() || !form.reason.trim()) { toast.error('Name and reason are required'); return; }
    setSaving(true);
    try {
      await api.addReferenceCall(projectId, form);
      toast.success(`${form.name.trim()} added`);
      setForm(EMPTY); setShowForm(false);
      load();
      setTimeout(load, 4000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
    setSaving(false);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Remove ${r.name} from experts?`)) return;
    try {
      await api.deleteReferenceCall(projectId, r.id);
      setRefs(prev => prev.filter(x => x.id !== r.id));
    } catch { toast.error('Failed to remove'); }
  };

  const inputCls = 'w-full bg-secondary/40 border border-border rounded-sm px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50';

  return (
    <CollapsibleSection
      storageKey={`deal.${projectId}.experts`}
      icon={PhoneCall}
      iconClass="text-teal-400"
      title="Experts"
      count={refs.length || undefined}
      testId="reference-calls-card"
      right={
        <button onClick={(e) => { e.stopPropagation(); setShowForm(v => !v); }} data-testid="refcall-add-btn"
          className="inline-flex items-center gap-1.5 text-[11px] text-teal-400 hover:text-teal-300 border border-teal-900/40 rounded-sm px-2.5 py-1.5 transition-colors">
          <Plus className="w-3 h-3" /> Add person
        </button>
      }
    >

      {showForm && (
        <div className="px-5 py-4 border-b border-border space-y-2" data-testid="refcall-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={form.name} onChange={set('name')} placeholder="Name *" data-testid="refcall-input-name" className={inputCls} />
            <input value={form.company} onChange={set('company')} placeholder="Company / role" data-testid="refcall-input-company" className={inputCls} />
            <input value={form.phone} onChange={set('phone')} placeholder="Phone" data-testid="refcall-input-phone" className={inputCls} />
            <input value={form.email} onChange={set('email')} placeholder="Email (optional)" data-testid="refcall-input-email" className={inputCls} />
          </div>
          <textarea value={form.reason} onChange={set('reason')} rows={2}
            placeholder="Why is this person relevant for this deal? *" data-testid="refcall-input-reason" className={inputCls} />
          <div className="flex items-center gap-2">
            <button onClick={handleAdd} disabled={saving} data-testid="refcall-save-btn"
              className="inline-flex items-center gap-1.5 text-[11px] font-medium bg-teal-700 hover:bg-teal-600 text-white rounded-sm px-3 py-2 transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Save
            </button>
            <button onClick={() => { setShowForm(false); setForm(EMPTY); }}
              className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-2 transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {refs.length === 0 && !showForm ? (
        <p className="px-5 py-4 text-xs text-muted-foreground">No experts yet — add people who know this company, sector or owner. Log actual calls with them via "Add Call / Meeting" (contact type: Expert).</p>
      ) : (
        <div className="divide-y divide-border/60">
          {refs.map(r => {
            return (
              <div key={r.id} className="px-5 py-3" data-testid={`refcall-row-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-medium text-foreground">{r.name}</span>
                  {r.company && <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Building2 className="w-3 h-3" />{r.company}</span>}
                  {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 text-[11px] text-teal-400 hover:text-teal-300"><Phone className="w-3 h-3" />{r.phone}</a>}
                  {r.email && <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 text-[11px] text-teal-400 hover:text-teal-300"><Mail className="w-3 h-3" />{r.email}</a>}
                  <button onClick={() => handleDelete(r)} data-testid={`refcall-delete-${r.id}`}
                    className="ml-auto p-1 text-muted-foreground/40 hover:text-red-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{r.reason}</p>
                <p className="text-[10px] text-muted-foreground/60 mt-0.5 font-mono">
                  added by {r.created_by || '—'} · {r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </CollapsibleSection>
  );
};
